import { BookingState } from '@/stores/booking';

type ErrorMessageObject = {
    copy: dynamicErrorCopy;
    button?: {
        text: string;
        page: string;
        newtab: boolean;
    };
};

type dynamicButtonLink = {
    text: string;
    page: string;
};

type dynamicErrorCopy = {
    title: string;
    body: string;
};

type EmailFriendlyVars = {
    clientName: string;
    gymName: string;
};

/**
 * Returns time of day based on users datetime on machine
 * @returns Time of day
 */
export const timeofday = (): string => {
    return new Date().getHours() < 12 ? 'Morning' : 'Afternoon';
};

/**
 * Creates an email string to automatically open for creation of zendesk ticket via email.
 * @param emailVars
 * @returns string
 */
export const composeEmail = (emailVars: EmailFriendlyVars): string => {
    return `mailto:enquiries@ultimateperformance.com?subject=${emailVars.clientName}%20-%20Issue%20with%20booking%20a%20consultation%20at%20${emailVars.gymName}%20Gym&body=Good%20${timeofday()}%2C%0A%0AI%20require%20some%20assistance%20as%20I%20am%20having%20issues%20booking%20my%20consultation%20at%20${emailVars.gymName}%20Gym.%0A%0AKind%20Regards.%0A`;
};

export const BOOKING_FAILURE_SUPPORT_EMAIL = 'support@ultimateperformance.com';

/**
 * Mailto link for users who could not complete a consultation booking.
 * Prefills subject and body so support can arrange the consultation.
 */
export const composeBookingFailureEmail = (): string => {
    return (
        `mailto:${BOOKING_FAILURE_SUPPORT_EMAIL}` +
        '?subject=Help%20with%20my%20consultation%20booking' +
        '&body=Hi%2C%0A%0AI%20tried%20to%20book%20a%20consultation%20through%20the%20Ultimate%20Performance%20website%20but%20unfortunately%20couldn%27t%20complete%20my%20booking.%0A%0AI%20would%20like%20to%20book%20a%20consultation%20for%3A%0A%0APreferred%20date%3A%0APreferred%20time%3A%0APreferred%20gym%20location%3A%0A%0APlease%20could%20someone%20help%20me%20arrange%20my%20consultation%3F%0A%0AThanks%21'
    );
};

/**
 * User-facing HTML shown when a consultation booking fails after valid form input.
 * Avoids exposing technical details such as HTTP status codes.
 */
export const getBookingFailureMessage = (): string => {
    const mailto = composeBookingFailureEmail();

    return (
        `<p class="font-bold mb-2">Oops! We couldn't complete your booking.</p>` +
        `<p class="mb-2">Something went wrong when trying to book your consultation.</p>` +
        `<p class="mb-2">Don't worry, we're here to help! Drop us an email at ` +
        `<a href="${mailto}" class="underline underline-offset-[3px]">${BOOKING_FAILURE_SUPPORT_EMAIL}</a> ` +
        `with your preferred date and time, and our team will get it sorted for you.</p>`
    );
};

/**
 * Returns Email Sender from bookingstate
 * @param {String|Boolean} name
 * @param {String|Boolean} email
 * @returns
 */
export const returnEmailSender = (
    name: string | boolean,
    email: string | boolean
): string => {
    let senderName = '';

    if (typeof name === 'string') {
        senderName = name;
    }

    if (typeof email === 'string') {
        senderName = email;
    }

    return senderName;
};

/**
 * Returns a string based on different state values
 * @param {Boolean} hasNoEnquiry If this booking started at the enquiry page or internal page
 * @param {boolean} existingBooking What mode the application is in
 * @param {String} requestId the request ID
 * @param {String} business the business ID
 * @returns Redirect URL
 */
export const returnErrorButton = (
    hasNoEnquiry: boolean,
    existingBooking: boolean,
    requestId: string | boolean,
    business: string | boolean
): dynamicButtonLink => {
    const noEnquiryUrl = {
        text: 'Start Again',
        page: '/up-staff-internal-use',
    };
    const bookingUrl = {
        text: 'Start Again',
        page: `/booking/${requestId}?business=${business}`,
    };
    const enquiryUrl = { text: 'Start Again', page: `/enquiry/${requestId}` };
    const guardedRoute = {
        text: 'Enquire Now',
        page: 'https://ultimateperformance.com',
    };

    const restartBookingUrl = existingBooking ? bookingUrl : enquiryUrl;
    const hasReference = requestId ? restartBookingUrl : guardedRoute;

    return hasNoEnquiry ? noEnquiryUrl : hasReference;
};

/**
 * Returns a string based on different state values
 * @param {Boolean} hasNoEnquiry If this booking started at the enquiry page or internal page
 * @param {boolean} existingBooking What mode the application is in
 * @param {String} requestId the request ID
 * @param {String} business the business ID
 * @returns Redirect URL
 */
export const returnErrorMessage = (
    hasNoEnquiry: boolean,
    requestId: string | boolean,
    business: string | boolean,
    emailString: string
): dynamicErrorCopy => {
    const standardError = {
        title: 'Something has gone wrong',
        body: `We're sorry, but it seems like there's a temporary issue preventing access to our consultation booking form. Our sincere apologies for any inconvenience caused, please contact <a href="${emailString}">enquiries@ultimateperformance.com</a> for help in booking your consultation.`,
    };
    const guardedRoute = {
        title: 'Invalid booking or enquiry reference.',
        body: "Let's get you set up with one. Please send an enquiry on our website and we'll send you an email to get you started.",
    };

    return !hasNoEnquiry && !requestId && !business
        ? guardedRoute
        : standardError;
};

/**
 * Refactored to receive the necessary data and computed properties/functions as arguments.
 * This makes the function independent of specific store implementations.
 *
 * @param {Object} bookingState - Represents the state from the booking store.
 * @param {Boolean} withNoEnquiry - Function to determine if there was no enquiry.
 * @param {Boolean} isModeExisting - Boolean represent an existing or new booking
 * @param {string} getRequestId - The request ID.
 * @param {string} getBusiness - The business ID.
 * @param {string} getMode - The current mode.
 */
export const getErrorData = (
    bookingState: BookingState,
    withNoEnquiry: boolean,
    isModeExisting: boolean,
    getRequestId: string | boolean,
    getBusiness: string | boolean
): ErrorMessageObject => {
    const emailVars = {
        clientName: encodeURIComponent(
            returnEmailSender(bookingState.first_name, bookingState.email)
        ),
        gymName: encodeURIComponent(bookingState.location),
    };

    const emailString = composeEmail(emailVars);

    const button = {
        ...returnErrorButton(
            withNoEnquiry,
            isModeExisting,
            getRequestId,
            getBusiness
        ),
        newtab: true,
    };

    const copy = returnErrorMessage(
        withNoEnquiry,
        getRequestId,
        getBusiness,
        emailString
    );

    return {
        button: { ...button },
        copy: { ...copy },
    };
};
